import { YouTube } from '@mui/icons-material';
import AccountBoxSharpIcon from '@mui/icons-material/AccountBoxSharp';
import ShareIcon from '@mui/icons-material/Share';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Box, Button, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { capitalCase } from 'change-case';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import HttpServices from 'src/services/httpService';
import { demoModuleUrl, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import SharePublicApprovalPopup from '../../../src/components/public-approval/share-approval.popup';
import EllapsedPost from './ellapsed-approval/ellapsed-post.component';
import PendingApproval from './pending-approval/pending-approval.component';
import RejectedApproval from './rejected-post/rejected-approval.component';
import { DemoHelper } from 'src/utils/demoUtil/demoUtil';

const Approval = () => {
  const { t: translate } = useTranslation();
  const [currentTab, setCurrentTab] = useState('pending approval');
  const theme = useTheme();
  const isCustomDomain = CommonHelper.CustomDomain();
  const isPortalAdmin = CommonHelper.GetBrandInfoFromLocalStorage("is_portal_admin");
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [shareApprovalPostsModal, setShareApprovalPostsModal] = useState(false);
  const [popoverElement, setPopoverElement] = useState(false);
  const [isLinkPresent, setLinkPresent] = useState(false);
  const [urlKey, setUrlKey] = useState('');
  const [shareUntil, setShareUntil] = useState('');
  const [isOrgEvent, setOrgEvent] = useState(CommonHelper.GetIsOrgLevelEvent());
  const hasOrgAdminPermission = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.ORG_ADMIN);
  const isShowOfferBanner = CommonHelper.ShowOfferBanner();
  const hasBrandAdminPermission = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.BRAND_ADMIN
  );
  const url = `${window.location.origin}/posts/${urlKey}`;
  const { showToast } = useToaster();

  useEffect(() => {
    hasBrandAdminPermission && !isOrgEvent && getShareApprovalPosts();
  }, []);

  const getShareApprovalPosts = () => {
    const getProps = {
      url: userModuleURL.getShareApprovalPosts,
      successCallback: getShareApprovalPostsSuccessCallback,
      failureCallback: getShareApprovalPostsFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const getShareApprovalPostsSuccessCallback = (data) => {
    setLinkPresent(data?.is_present);
    setUrlKey(data?.url_key);
    setShareUntil(data?.expire_date);
  };

  const getShareApprovalPostsFailureCallback = (message) => {
    message && showToast(message, 'e');
  };

  const handleOpenMenu = (event) => {
    setPopoverElement(event.currentTarget);
    setShareApprovalPostsModal(true);
  };

  const handleCloseMenu = () => {
    setPopoverElement(null);
    setShareApprovalPostsModal(false);
  };
  const shareApprovalPostProps = {
    popoverElement: popoverElement,
    popoverElement,
    url: url,
    urlKey: urlKey,
    shareUntil: shareUntil,
    handleCloseMenu,
    getShareApprovalPosts,
  };

  const ACCOUNT_TABS = [
    {
      value: 'pending approval',
      icon: <AccountBoxSharpIcon sx={{ width: '20px', height: '20px' }} />,
      component: <PendingApproval isOrgEvent={isOrgEvent} isShowOfferBanner={isShowOfferBanner}/>,
    },
    {
      value: 'rejected approval',
      icon: <VpnKeyIcon sx={{ width: '20px', height: '20px' }} />,
      component: <RejectedApproval isOrgEvent={isOrgEvent} isShowOfferBanner={isShowOfferBanner}/>,
    },
    {
      value: 'Ellapsed Requests',
      icon: <VpnKeyIcon sx={{ width: '20px', height: '20px' }} />,
      component: <EllapsedPost isOrgEvent={isOrgEvent} isShowOfferBanner={isShowOfferBanner}/>,
    },
  ];
  const handleOrgConnectedProfiles = (event) => {
    const checked = event.target.checked;
    setOrgEvent(checked);
    CommonHelper.SetIsOrgLevelEvent(checked);
  };
  return (
    <>
      <Helmet>
        <title>Approval</title>
      </Helmet>
      {hasOrgAdminPermission &&  !DemoHelper.IsDemoAccount() &&(
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1, // add bottom margin for spacing
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary', // use theme-based text color
            }}
          >
            {translate('common.enable_org_level_posts', 'Enable Org Level Posts')}
          </Typography>
          <StyledComponent.SwitchIcon size="small" checked={isOrgEvent} onChange={handleOrgConnectedProfiles} />
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Tabs
          value={currentTab}
          scrollButtons="auto"
          variant="scrollable"
          sx={{ gap: '0.5rem', minHeight: '37px' }}
          allowScrollButtonsMobile
          onChange={(e, value) => setCurrentTab(value)}
        >
          {ACCOUNT_TABS.map((tab) => {
            return (
              <Tab
                disableRipple
                key={tab.value}
                label={isMobile ? '' : capitalCase(tab.value)}
                icon={tab.icon}
                value={tab.value}
                sx={{
                  flexDirection: 'row',
                  gap: '0.25rem',
                  padding: 0,
                  minHeight: '10px',
                  mr: '0.75rem',
                  fontWeight: 600,
                }}
              />
            );
          })}
        </Tabs>
        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {translate('common.approval_workflow', 'Approval Workflow')}
            </Typography>
            {((!isCustomDomain || isPortalAdmin))  &&(
              <Button
                variant="outlined"
                startIcon={<YouTube />}
                onClick={() =>
                  window.open('https://www.youtube.com/watch?v=uEh6y85D_9g&ab_channel=Brand2Social', '_blank')
                }
                color="primary"
              >
                {translate('common.tutorial', 'Tutorial')}
              </Button>
            )}
          </Stack>
          {!isOrgEvent && (
            <Button variant="contained" startIcon={<ShareIcon />} onClick={(e) => handleOpenMenu(e)}>
              {isLinkPresent ? translate('approval.link_already_shared', 'Link Already Shared') : translate('approval.share_public_link', 'Share Public Link')}
            </Button>
          )}
        </Stack>
      </Box>
      <SharePublicApprovalPopup {...shareApprovalPostProps} />
      <Box sx={{ mb: 2 }} />
      {ACCOUNT_TABS.map((tab) => {
        const isMatched = tab.value === currentTab;
        return isMatched && <Box key={tab.value}>{tab.component}</Box>;
      })}
    </>
  );
};
export default Approval;
